import { request } from '@/api/http';

export type ConfigImportResult = {
  updated: string[];
  ignored: string[];
  unknown: string[];
  warnings: { key: string; message: string }[];
  sensitiveUpdated: string[];
};

export type ConfigDefinition = {
  key: string;
  category: string;
  categoryLabel: string;
  valueType: 'string' | 'integer' | 'float' | 'boolean';
  defaultValue: unknown;
  description: string;
  minValue?: number | null;
  maxValue?: number | null;
  restartRequired: boolean;
  editable: boolean;
  visible: boolean;
  value: unknown;
  isDefault: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type ConfigDefinitionsResponse = {
  definitions: ConfigDefinition[];
  categories: Record<string, string>;
};

export type ConfigMetrics = {
  queueSize: number;
  runningJobs: number;
  failedJobs: number;
  completedJobs: number;
  averageScrapeDurationSeconds: number;
  averageSendDurationSeconds: number;
  messagesPerHour: number;
  linksFoundPerHour: number;
  retryRatePercent: number;
  failureRatePercent: number;
  workerUtilisation: number;
  averageSearchDurationSeconds: number;
};

export type ConfigTestResult = {
  status: string;
  message: string;
  details?: Record<string, unknown>;
};

export const configService = {
  get: () => request<{ data: Record<string, unknown>; restart_required: boolean }>('/config'),
  getDefinitions: () => request<ConfigDefinitionsResponse>('/config/definitions'),
  getMetrics: () => request<ConfigMetrics>('/config/metrics'),
  patch: (data: Record<string, unknown>) =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>('/config', {
      method: 'PATCH',
      body: JSON.stringify({ data }),
    }),
  bulk: (data: Record<string, unknown>) =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>('/config/bulk', {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
  exportJson: () =>
    request<{ version: number; exported_at: string; categories: string[]; values: Record<string, unknown> }>(
      '/config/export'
    ),
  importJson: (payload: Record<string, unknown>) =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>('/config/import-json', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  resetCategory: (category: string) =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>(`/config/reset/${category}`, {
      method: 'POST',
    }),
  resetAll: () =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>('/config/reset-all', {
      method: 'POST',
    }),
  bootstrap: () =>
    request<{ data: Record<string, unknown>; restart_required: boolean }>('/config/bootstrap', {
      method: 'POST',
    }),
  runTest: (action: string, extras?: { searchId?: string; sourceName?: string }) =>
    request<ConfigTestResult>('/config/tests/run', {
      method: 'POST',
      body: JSON.stringify({ action, ...extras }),
    }),
  importEnv: (envText: string) =>
    request<ConfigImportResult>('/config/import-env', {
      method: 'POST',
      body: JSON.stringify({ envText }),
    }),
  reload: () => request<{ status: string; message: string }>('/config/reload', { method: 'POST' }),
};
